import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DropdownMenu from '../ui/DropdownMenu';
import { useToast } from '../ui';
import ConfirmationModal from '../ui/ConfirmationModal';
import { invoiceAPI } from '../../services/api';
import { invoiceStatus, canMarkInvoicePaid } from './invoiceLifecycle';
import MarkPaidConfirm from './MarkPaidConfirm';
import SendAgainComposer from './SendAgainComposer';

/**
 * Items for an invoice row's ⋮ (IDI-518), by stored status. Past due is a
 * Sent invoice's aging, so it gets Mark paid too. No one-click send from a
 * row (IDI-538); no Mark Unsent, Void or Cancel anywhere. Send again
 * (IDI-580) follows the status item on Sent, Past due and Paid rows and
 * opens IDI-485's composer pre-filled from the last send.
 *
 * @param {object} invoice
 * @returns {Array<'review'|'mark_paid'|'mark_unpaid'|'send_again'>}
 */
export function invoiceRowActions(invoice) {
  const s = invoiceStatus(invoice);
  if (s === 'draft') return ['review'];
  if (canMarkInvoicePaid(invoice)) return ['mark_paid', 'send_again'];
  if (s === 'paid') return ['mark_unpaid', 'send_again'];
  return [];
}

/**
 * InvoiceRowMenu — the shared row ⋮ on the job's and the customer's
 * Invoices lists. Lives in a cell that stops propagation so the row click
 * still opens the invoice (QuotesPage.js row-menu pattern). `onChanged`
 * refreshes the list in place after Mark paid / Mark unpaid.
 */
export default function InvoiceRowMenu({ invoice, onChanged }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [unpaidOpen, setUnpaidOpen] = useState(false);
  const [sendAgainOpen, setSendAgainOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const number = invoice?.invoiceNumber || invoice?.invoice_number || 'Invoice';
  const actions = invoiceRowActions(invoice);
  if (!invoice?.id || actions.length === 0) return null;

  // The list refresh after a successful action must never surface as an
  // unhandled rejection (or as a failure of the action that did succeed).
  const refresh = async () => {
    try {
      await onChanged?.();
    } catch (e) {
      console.error('[InvoiceRowMenu] list refresh failed', e);
    }
  };

  const markPaid = async (paidDate) => {
    setBusy(true);
    try {
      await invoiceAPI.markPaid(invoice.id, paidDate);
    } catch {
      setMarkPaidOpen(false);
      setBusy(false);
      toast.error(`Failed to mark ${number} paid.`);
      return;
    }
    setMarkPaidOpen(false);
    setBusy(false);
    toast.success(`${number} marked paid`);
    await refresh();
  };

  const markUnpaid = async () => {
    setBusy(true);
    try {
      await invoiceAPI.markUnpaid(invoice.id);
    } catch {
      setUnpaidOpen(false);
      setBusy(false);
      toast.error(`Failed to mark ${number} unpaid.`);
      return;
    }
    setUnpaidOpen(false);
    setBusy(false);
    toast.success(`${number} marked unpaid`);
    await refresh();
  };

  const items = actions.map((a) => {
    if (a === 'review') {
      // IDI-566: Review & Send returns to /jobs/<id>?tab=invoices.
      return { label: 'Review & Send', onClick: () => navigate(`/invoices/${invoice.id}/review`) };
    }
    if (a === 'mark_paid') return { label: 'Mark paid', onClick: () => setMarkPaidOpen(true), disabled: busy };
    if (a === 'send_again') return { label: 'Send again', onClick: () => setSendAgainOpen(true), disabled: busy };
    return { label: 'Mark unpaid', onClick: () => setUnpaidOpen(true), disabled: busy };
  });

  return (
    <>
      <DropdownMenu ariaLabel={`Actions for ${number}`} stopPropagation items={items} />
      {sendAgainOpen && (
        // Inside the row: keep the composer's clicks from opening the invoice.
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div onClick={(e) => e.stopPropagation()}>
          <SendAgainComposer
            invoice={invoice}
            onClose={() => setSendAgainOpen(false)}
            onSent={refresh}
          />
        </div>
      )}
      {markPaidOpen && (
        <MarkPaidConfirm
          open={markPaidOpen}
          invoiceNumber={number}
          busy={busy}
          onConfirm={markPaid}
          onClose={() => setMarkPaidOpen(false)}
        />
      )}
      {unpaidOpen && (
        <ConfirmationModal
          open={unpaidOpen}
          title={`Mark ${number} unpaid?`}
          message="This returns the invoice to Sent and clears its paid date."
          confirmLabel="Mark unpaid"
          busy={busy}
          onConfirm={markUnpaid}
          onClose={() => setUnpaidOpen(false)}
        />
      )}
    </>
  );
}
